<script setup lang="ts">
import { pSelectTriggerCva, type PSelectTriggerVariants } from '@ui/components/core/p-select/index.js';
import type { SelectTriggerProps } from 'reka-ui';
import type { HTMLAttributes } from 'vue';
import { reactiveOmit } from '@vueuse/core';
import { IconChevronDown } from '@tabler/icons-vue';
import { SelectIcon, SelectTrigger, useForwardProps } from 'reka-ui';
import { cn } from '@ui/lib/utils.js';

const props = withDefaults(
    defineProps<
        SelectTriggerProps & {
            size?: PSelectTriggerVariants['size'];
            class?: HTMLAttributes['class'];
        }
    >(),
    { size: 'default' },
);

const delegatedProps = reactiveOmit(props, 'class', 'size');
const forwardedProps = useForwardProps(delegatedProps);
</script>

<template>
    <SelectTrigger
        data-slot="select-trigger"
        :data-size="size"
        v-bind="forwardedProps"
        :class="
            cn(
                pSelectTriggerCva({ size: props.size }),
                'border-border-default',
                'data-[placeholder]:text-foreground-muted',
                '[&_svg:not([class*=\'text-\'])]:text-foreground-muted',
                'focus-visible:border-ring-default',
                'focus-visible:ring-ring-default',
                'aria-invalid:ring-ring-error',
                'aria-invalid:border-border-destructive',
                'flex',
                'w-fit',
                'items-center',
                'justify-between',
                'gap-2',
                'rounded-lg',
                'border',
                'bg-surface-input',
                'paragraph-sm',
                'text-foreground-primary',
                'whitespace-nowrap',
                'shadow-xs',
                'transition-[color,box-shadow]',
                'outline-none',
                'focus-visible:ring-3',
                'disabled:cursor-not-allowed',
                'disabled:opacity-50',
                'data-[size=sm]:h-8',
                '*:data-[slot=select-value]:line-clamp-1',
                '*:data-[slot=select-value]:flex',
                '*:data-[slot=select-value]:items-center',
                '*:data-[slot=select-value]:gap-2',
                '[&_svg]:pointer-events-none',
                '[&_svg]:shrink-0',
                '[&_svg:not([class*=\'size-\'])]:size-4',
                props.class,
            )
        "
    >
        <slot />
        <SelectIcon as-child>
            <IconChevronDown class="size-4 opacity-50" />
        </SelectIcon>
    </SelectTrigger>
</template>
